/* eslint-disable sonarjs/cognitive-complexity */
import { useState, useCallback, useEffect } from 'react'
import { CarHireLocation, getDropOffBrandFilter } from '@reward-platform/ancillaries-locations'
import { UseFormReturn, useWatch } from 'react-hook-form'
import { useDebounceValue } from 'usehooks-ts'
import { useFeatureFlag } from '~/context/featureFlag'
import { useAncillariesLocationsSearch } from '~/hooks/useAncillariesLocations'
import { CarHireSearchForm, CarHireLocationOption } from '../SearchCarHireForm.types'

const EMPTY_FORM_STATE = {
  type: '',
  id: '',
  fullName: '',
  name: '',
  countryCode: '',
  address: [],
  vendorBrandId: '',
}

const INITIAL_DEFAULT_STATE = null

/**
 * Set up and manage state for the locations autocomplete fields
 */
export function useCarHireSearchFormLocation(
  form: UseFormReturn<CarHireSearchForm>,
  defaultState: CarHireSearchForm['pickUpSearchString'] | CarHireSearchForm['dropOffSearchString'],
  fieldName: keyof CarHireSearchForm,
  controllerFieldName: keyof CarHireSearchForm
): CarHireLocationOption {
  const multiBrandEnabled = useFeatureFlag('car-hire-multi-brand')
  const [searchString, setSearchString] = useState<string | null>(
    defaultState || INITIAL_DEFAULT_STATE
  )

  useEffect(() => {
    setSearchString(defaultState || INITIAL_DEFAULT_STATE)
  }, [defaultState])

  const controllerFieldValue = useWatch({
    control: form.control,
    name: controllerFieldName,
  }) as string

  const [debouncedSearchString] = useDebounceValue(searchString ?? '', 500)

  // When re-focusing the input field, pause re-fetching until user edits the query and instead show previous data.
  const fullNameSelected = searchString === controllerFieldValue && form.getValues(fieldName)
  const canSearch =
    debouncedSearchString !== '' && debouncedSearchString === searchString && !fullNameSelected

  // Filter by the pick-up's brand SET, never by its single vendorBrandId: a consolidated row
  // always reports "3" there, which would hide every Budget-only drop-off from a pick-up that
  // actually serves both brands.
  const pickUpVendorBrandId =
    controllerFieldName === 'dropOffSearchString'
      ? getDropOffBrandFilter(form.getValues('pickUpLocation'))
      : undefined

  const { data: locations, isLoading } = useAncillariesLocationsSearch(
    'CAR_HIRE',
    {
      searchString: debouncedSearchString,
      vendorBrandId: pickUpVendorBrandId,
      ...(multiBrandEnabled ? { multiBrand: true } : {}),
    },
    { isEnabled: canSearch }
  )

  const noResultsFound = locations?.length === 0 && !isLoading && canSearch

  const clearLocation = useCallback(() => {
    form.setValue(fieldName, undefined)
    form.setValue(controllerFieldName, '')
    setSearchString('')
  }, [controllerFieldName, fieldName, form])

  const onInputFocus = useCallback(
    // Use shorter name as search query when re-focusing input field, as fullName will usually not yield results. Could be removed if search is enhanced in future.
    (e: { target: { select: () => void } }, openMenu: () => void) => {
      e.target.select()

      openMenu()

      if (searchString === '') {
        clearLocation()
      }
    },
    [clearLocation, searchString]
  )

  const handleAutocompleteChange = useCallback(
    (value?: string) => {
      const definedValue = value ?? ''

      setSearchString(definedValue)
      if (definedValue.length === 0) {
        form.setValue(fieldName, EMPTY_FORM_STATE, { shouldDirty: true })
      } else if (
        multiBrandEnabled &&
        definedValue !== controllerFieldValue &&
        (form.getValues(fieldName) as CarHireLocation | undefined)?.fullName !== definedValue
      ) {
        // Typing a replacement invalidates the previously selected location. Keeping it in the
        // form lets submitForm use the old location if the new autocomplete selection has not yet
        // been committed, which is especially dangerous when the old/new locations have different
        // serving-brand sets.
        form.setValue(fieldName, undefined, { shouldDirty: true })
      }
    },
    [controllerFieldValue, fieldName, form, multiBrandEnabled]
  )

  const handleSuggestionClick = useCallback(
    (key: string) => {
      const location = (locations as CarHireLocation[]).find(
        (l: CarHireLocation & { key?: string }) => l.key === key
      )
      if (location) {
        setSearchString(location.fullName)
        form.clearErrors(controllerFieldName)
        form.setValue(fieldName, location, { shouldDirty: true })
        form.setValue(controllerFieldName, location.fullName, { shouldDirty: true })
      }
    },
    [locations, form, fieldName, controllerFieldName]
  )

  const swapLocations = useCallback(
    (newName: string, newLocation: CarHireLocation) => {
      form.setValue(fieldName, newLocation)
      form.setValue(controllerFieldName, newName)
      setSearchString(newName)
    },
    [controllerFieldName, fieldName, form]
  )

  return {
    isLoading,
    locations: isLoading || !canSearch || !locations ? [] : locations,
    searchString: searchString ?? '',
    setSearchString,
    noResultsFound,
    clearLocation,
    onInputFocus,
    handleAutocompleteChange,
    handleSuggestionClick,
    swapLocations,
  }
}
